import { MeasureEntity, View } from "@novorender/api";
import { ReadonlyVec2, ReadonlyVec3, vec2, vec3 } from "gl-matrix";

export type PointSnapInfo = {
    connection: ReadonlyVec3;
} & (
    | {
          type: "vertex";
      }
    | {
          type: "corner";
          guides: ReadonlyVec3[]; // not normals, actual points in 3D space
      }
    | {
          type: "tangent";
          start: ReadonlyVec3;
          end: ReadonlyVec3;
      }
    | {
          type: "midpoint";
          start: ReadonlyVec3;
          end: ReadonlyVec3;
      }
    | {
          type: "normal";
          normal: ReadonlyVec3;
      }
);

export type PointSnapInfo2d = {
    connection: ReadonlyVec2;
} & (
    | {
          type: "vertex";
      }
    | {
          type: "corner";
          guides: (ReadonlyVec2 | undefined)[];
      }
    | {
          type: "tangent";
          start: ReadonlyVec2 | undefined;
          end: ReadonlyVec2 | undefined;
      }
    | {
          type: "midpoint";
          start: ReadonlyVec2 | undefined;
          end: ReadonlyVec2 | undefined;
      }
    | {
          type: "normal";
          normal: ReadonlyVec2 | undefined;
      }
);

export async function getSnapInfo({
    view,
    entity,
    connectionPoint,
    corners = false,
}: {
    view: View;
    entity: MeasureEntity;
    connectionPoint: ReadonlyVec3;
    corners?: boolean;
}): Promise<PointSnapInfo | undefined> {
    if (entity.drawKind === "vertex" && corners) {
        const guides: ReadonlyVec3[] = [];
        const edges = await view.measure?.core.getVertexEdges(entity);
        if (edges?.length) {
            for (const edge of edges) {
                if (edge.kind === "line") {
                    // TODO for some reason irrelevant points may be returned, check getVertexEdges
                    const isStartVertex = vec3.dist(edge.start, connectionPoint) < 1e-6;
                    const isEndVertex = vec3.dist(edge.end, connectionPoint) < 1e-6;
                    if (!isStartVertex && !isEndVertex) {
                        continue;
                    }
                    const point = isStartVertex ? edge.end : edge.start;
                    const guide = vec3.sub(vec3.create(), point, connectionPoint);
                    vec3.normalize(guide, guide);
                    vec3.add(guide, guide, connectionPoint);
                    guides.push(guide);
                }
            }
        }
        return { connection: connectionPoint, type: "corner", guides };
    } else if (entity.drawKind === "vertex") {
        return {
            connection: connectionPoint,
            type: "vertex",
        };
    } else if (entity.tangent) {
        // Check to see if the snap is the middle of the tangent
        const start = vec3.add(vec3.create(), connectionPoint, entity.tangent);
        const end = vec3.scaleAndAdd(vec3.create(), connectionPoint, entity.tangent, -1);

        return {
            connection: connectionPoint,
            type: entity.curvePart === "edgeMidpoint" ? "midpoint" : "tangent",
            start,
            end,
        };
    } else if (entity.normal) {
        return {
            connection: connectionPoint,
            type: "normal",
            normal: entity.normal,
        };
    } else {
        return {
            connection: connectionPoint,
            type: "vertex",
        };
    }
}

export function getSnapInfo2d(
    view: View,
    toScreenSpace: (p: ReadonlyVec3) => vec2 | undefined,
    snapInfo: PointSnapInfo,
): PointSnapInfo2d | undefined {
    const connection2d = toScreenSpace(snapInfo.connection);
    const guides2d: (ReadonlyVec2 | undefined)[] = [];

    if (!connection2d) {
        return;
    }

    if (snapInfo.type === "corner") {
        snapInfo.guides.forEach((guide, index) => {
            const guide2d = toScreenSpace(guide);
            if (!guide2d) {
                guides2d.push(undefined);
                return;
            }

            vec2.sub(guide2d, guide2d, connection2d);
            vec2.normalize(guide2d, guide2d);

            guides2d[index] = guide2d;
        });

        return { connection: connection2d, type: "corner", guides: guides2d };
    } else if (snapInfo.type === "tangent") {
        return {
            connection: connection2d,
            type: "tangent",
            start: toScreenSpace(snapInfo.start),
            end: toScreenSpace(snapInfo.end),
        };
    } else if (snapInfo.type === "midpoint") {
        return {
            connection: connection2d,
            type: "midpoint",
            start: toScreenSpace(snapInfo.start),
            end: toScreenSpace(snapInfo.end),
        };
    } else if (snapInfo.type === "normal") {
        const point = vec3.add(vec3.create(), snapInfo.connection, snapInfo.normal);
        const normal = toScreenSpace(point);
        if (normal) {
            vec2.sub(normal, normal, connection2d);
        }

        return {
            connection: connection2d,
            type: "normal",
            normal,
        };
    } else {
        return {
            connection: connection2d,
            type: "vertex",
        };
    }
}

export function getSnapGuides(snap: PointSnapInfo) {
    if (snap.type === "corner") {
        return snap.guides;
    } else if (snap.type === "tangent" || snap.type === "midpoint") {
        const guides: ReadonlyVec3[] = [];
        if (snap.start) {
            guides.push(snap.start);
        }
        if (snap.end) {
            guides.push(snap.end);
        }
        return guides;
    }
    return [];
}

export function getSnapGuides2d(snap: PointSnapInfo2d) {
    if (snap.type === "corner") {
        return snap.guides.filter(Boolean) as ReadonlyVec2[];
    } else if (snap.type === "tangent" && snap.start && snap.end) {
        const dir = vec2.sub(vec2.create(), snap.end, snap.start);
        vec2.normalize(dir, dir);
        return [dir, vec2.negate(vec2.create(), dir)];
    } else if (snap.type === "midpoint" && snap.start && snap.end) {
        const dir = vec2.sub(vec2.create(), snap.end, snap.start);
        vec2.normalize(dir, dir);
        vec2.rotate(dir, dir, [0, 0], Math.PI / 2);
        return [dir, vec2.negate(vec2.create(), dir)];
    }
    return [];
}
